import { z } from 'zod';
import { addressSchema, evmChainSlugSchema } from './common.js';
export const tokenIdSchema = z.string().regex(/^\d+$/, 'Expected a non-negative integer token id');
export const addressBatchSchema = z.array(addressSchema).min(1).max(100, 'Maximum 100 addresses per batch');
export const tokenIdBatchSchema = z.array(tokenIdSchema).min(1).max(100, 'Maximum 100 token ids per batch');
export const getNftCollectionParamsSchema = z.object({
    chain: evmChainSlugSchema,
    contract: addressSchema,
    blockHeight: z.number().int().nonnegative().optional(),
}).strict();
export const getNftBalanceParamsSchema = z.object({
    chain: evmChainSlugSchema,
    contract: addressSchema,
    address: addressSchema,
    blockHeight: z.number().int().nonnegative().optional(),
}).strict();
export const getNftTokensParamsSchema = z.object({
    chain: evmChainSlugSchema,
    contract: addressSchema,
    address: addressSchema,
    blockHeight: z.number().int().nonnegative().optional(),
}).strict();
export const getNftMetadataParamsSchema = z.object({
    chain: evmChainSlugSchema,
    contract: addressSchema,
    tokenId: tokenIdSchema,
    blockHeight: z.number().int().nonnegative().optional(),
}).strict();
export const getNftTokenOwnerParamsSchema = z.object({
    chain: evmChainSlugSchema,
    contract: addressSchema,
    tokenId: tokenIdSchema,
    blockHeight: z.number().int().nonnegative().optional(),
}).strict();
export const getNftErc1155BalanceParamsSchema = z.object({
    chain: evmChainSlugSchema,
    contract: addressSchema,
    address: addressSchema,
    tokenId: tokenIdSchema,
    blockHeight: z.number().int().nonnegative().optional(),
}).strict();
export const getNftBatchBalanceParamsSchema = z.object({
    chain: evmChainSlugSchema,
    contract: addressSchema,
    addresses: addressBatchSchema,
    tokenIds: tokenIdBatchSchema,
    blockHeight: z.number().int().nonnegative().optional(),
}).strict().superRefine(({ addresses, tokenIds }, context) => {
    if (addresses.length !== tokenIds.length) {
        context.addIssue({
            code: 'custom',
            path: ['tokenIds'],
            message: 'addresses and tokenIds must have the same length',
        });
    }
});
export const nftStandardSchema = z.object({
    erc721: z.boolean(),
    erc721Enumerable: z.boolean(),
    erc1155: z.boolean(),
}).strict();
export const nftCollectionDataSchema = z.object({
    chain: evmChainSlugSchema,
    contract: addressSchema,
    name: z.union([z.string().min(1), z.null()]),
    symbol: z.union([z.string().min(1), z.null()]),
    totalSupply: z.union([z.string().min(1), z.null()]),
    standard: z.union([nftStandardSchema, z.null()]).optional(),
}).strict();
export const nftBalanceDataSchema = z.object({
    chain: evmChainSlugSchema,
    contract: addressSchema,
    address: addressSchema,
    balance: z.union([z.string().min(1), z.number().int().nonnegative()]),
    tokenId: tokenIdSchema.optional(),
}).strict();
export const nftTokensDataSchema = z.object({
    chain: evmChainSlugSchema,
    contract: addressSchema,
    address: addressSchema,
    tokenIds: z.array(tokenIdSchema).optional(),
    tokens: z.array(tokenIdSchema).optional(),
    total: z.union([z.string().min(1), z.number().int().nonnegative()]).optional(),
    capped: z.boolean().optional(),
}).strict().refine((value) => value.tokenIds !== undefined || value.tokens !== undefined, { message: 'Expected tokenIds or tokens in NFT token listings' });
export const nftBatchBalanceEntrySchema = z.object({
    address: addressSchema,
    tokenId: tokenIdSchema,
    balance: z.string().min(1),
}).strict();
export const nftBatchBalanceDataSchema = z.object({
    chain: evmChainSlugSchema,
    contract: addressSchema,
    results: z.array(nftBatchBalanceEntrySchema).optional(),
    balances: z.array(z.union([z.string().min(1), z.number().int().nonnegative()])).optional(),
}).strict().refine((value) => value.results !== undefined || value.balances !== undefined, { message: 'Expected results or balances in batch NFT balance response' });
export const nftTokenOwnerDataSchema = z.object({
    chain: evmChainSlugSchema,
    contract: addressSchema,
    tokenId: tokenIdSchema,
    owner: addressSchema,
}).strict();
export const nftMetadataDataSchema = z.object({
    chain: evmChainSlugSchema,
    contract: addressSchema,
    tokenId: tokenIdSchema,
    standard: nftStandardSchema.optional(),
    owner: z.union([addressSchema, z.null()]).optional(),
    tokenURI: z.union([z.string().min(1), z.null()]).optional(),
    uri: z.union([z.string().min(1), z.null()]).optional(),
    metadata: z.unknown().optional(),
}).strict();
